import { useMemo } from 'react';
import { ResourceGrid } from './ResourceGrid';
import { useProject } from '../hooks/useProject';
import { hasPermission } from '../types/project';
import { createSearchDatasource, createAdvancedSearchDatasource } from '../services/resourceDatasource';
import type { BulkSearch } from '../types/search';

/**
 * SearchResults — the results grid for a basic or advanced search, project-wide or
 * scoped to one collection. The React half of SearchController: the Angular nav input
 * and advanced builder still produce the query (Wave 4 migrates those); this renders
 * whatever they executed.
 *
 * Re-pointing the grid on each new search is just the datasource memo — ResourceGrid
 * purges its cache when the datasource identity changes.
 */
export function SearchResults({
  projectId,
  collectionId,
  search,
}: {
  projectId: string;
  collectionId?: string;
  search: BulkSearch;
}) {
  const { data: project } = useProject(projectId);

  const datasource = useMemo(
    () =>
      search.advanced
        ? createAdvancedSearchDatasource({ projectId, collectionId, expression: search.expression })
        : createSearchDatasource({ projectId, collectionId, query: search.query }),
    [projectId, collectionId, search]
  );

  const bulkContext = useMemo(
    () => ({
      projectId,
      projectName: project?.name,
      // SearchController:163, :196 — `scope.collection ? [scope.collection.id] : []`.
      collectionIds: collectionId ? [collectionId] : [],
      // Angular reads `scope.cookieFilter` here, which SearchController declares but
      // never assigns — search results carry no tag filter. `search` scopes all-mode.
      filter: null,
      search,
      canManageTags: !!project && hasPermission(project, 'MANAGE_TAGS'),
      canCreateBatches: !!project && hasPermission(project, 'MANAGE_BATCHES'),
      canManageReports: !!project && hasPermission(project, 'MANAGE_REPORTS'),
    }),
    [projectId, collectionId, search, project]
  );

  // No tagFilter prop: search results have no tag-filter panel in Angular.
  return <ResourceGrid datasource={datasource} bulkContext={bulkContext} />;
}
